import "./App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import AppLayout from "./components/AppLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import NetworkBanner from "./components/NetworkBanner";
import InstallPrompt from "./components/InstallPrompt";
import UpdatePrompt from "./components/UpdatePrompt";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BrandingProvider } from "./context/BrandingContext";
import { NetworkProvider } from "./context/NetworkContext";
import { startSyncScheduler } from "./lib/syncManager";
import { registerSW } from "./lib/swRegistration";

const AuthPage = lazy(() => import("./pages/AuthPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const GamePage = lazy(() => import("./pages/GamePage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const RulesPage = lazy(() => import("./pages/RulesPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const StatsDetailPage = lazy(() => import("./pages/StatsDetailPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const TeeTimesPage = lazy(() => import("./pages/TeeTimesPage"));
const MyBookingsPage = lazy(() => import("./pages/MyBookingsPage"));
const PlayerProfilePage = lazy(() => import("./pages/PlayerProfilePage"));
const StartGamePage = lazy(() => import("./pages/StartGamePage"));

const PageFallback = () => (
  <div className="flex items-center justify-center py-12">
    <div className="h-8 w-8 rounded-full border-2 border-[#dfa625] border-t-transparent animate-spin" />
  </div>
);

function PublicEntryRoute() {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  return user ? (
    <Navigate to="/dashboard" replace />
  ) : (
    <Suspense fallback={<PageFallback />}>
      <AuthPage />
    </Suspense>
  );
}

const P = ({ children, requireAdmin }) => (
  <ProtectedRoute requireAdmin={requireAdmin}>
    <AppLayout>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </AppLayout>
  </ProtectedRoute>
);

function AppRoutes() {
  // Start background sync scheduler and register service worker
  useEffect(() => {
    startSyncScheduler();
    registerSW();
  }, []);

  return (
    <Routes>
      <Route path="/auth" element={<PublicEntryRoute />} />
      <Route path="/forgot-password" element={<Suspense fallback={<PageFallback />}><ForgotPasswordPage /></Suspense>} />
      <Route path="/reset-password" element={<Suspense fallback={<PageFallback />}><ResetPasswordPage /></Suspense>} />
      <Route path="/dashboard" element={<P><DashboardPage /></P>} />
      <Route path="/game/start" element={<P><StartGamePage /></P>} />
      <Route path="/game/new" element={<P><GamePage /></P>} />
      <Route path="/game/:roundId/edit" element={<P><GamePage /></P>} />
      <Route path="/history" element={<P><HistoryPage /></P>} />
      <Route path="/rules" element={<P><RulesPage /></P>} />
      <Route path="/profile" element={<P><ProfilePage /></P>} />
      <Route path="/settings" element={<P><SettingsPage /></P>} />
      <Route path="/stats/:metricKey" element={<P><StatsDetailPage /></P>} />
      <Route path="/tee-times" element={<P><TeeTimesPage /></P>} />
      <Route path="/tee-times/my-bookings" element={<P><MyBookingsPage /></P>} />
      <Route path="/players/:userId" element={<P><PlayerProfilePage /></P>} />
      <Route path="/admin" element={<P requireAdmin><AdminPage /></P>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <NetworkProvider>
          <BrowserRouter>
            <NetworkBanner />
            <AppRoutes />
            <InstallPrompt />
            <UpdatePrompt />
            <Toaster position="top-right" richColors closeButton />
          </BrowserRouter>
        </NetworkProvider>
      </AuthProvider>
    </BrandingProvider>
  );
}
